import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  DUAL_SCOPE_TABLES,
  DUAL_SCOPE_TYPE_ERROR_CODES,
  pickDualScopeFields,
  type DualScopeTableName,
  type DualScopeTypeDeleteResponse,
  type DualScopeTypeErrorCode,
  type DualScopeTypeItemResponse,
  type DualScopeTypeListResponse,
} from '@releve/shared';
import { authedDelete, authedGet, authedPatch, authedPost } from '@/lib/api';
import { useOrganization } from '@/contexts/OrganizationContext';
import { toast } from '@/hooks/useToast';
import i18n from '@/i18n';

/**
 * Dual-scope type lists (global rows + the org's own) — read and written
 * through the Hono backend at `/api/v1/dual-scope-types` (docs/DUAL_SCOPE_TYPES.md).
 *
 * Global rows (`organization_id NULL`) are written by super admins only; org
 * rows by org admins only (no super-admin bypass). The route enforces both and
 * answers with the coded errors in `DUAL_SCOPE_TYPE_ERROR_CODES`.
 */

export interface DualScopeType {
  id: string;
  organization_id: string | null;
  code: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  sort_order: number | null;
  is_active: boolean | null;
  is_default: boolean | null;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  is_global?: boolean;  // Computed: true if org_id is null
}

export interface CreateDualScopeTypeData {
  /** Optional: the route derives a slug from `name` when absent or blank. */
  code?: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  sort_order?: number;
  is_default?: boolean;
  metadata?: Record<string, unknown>;
}

/**
 * The list scope that reads/writes GLOBAL rows only, through the org-less
 * super-admin route `/api/v1/dual-scope-types/global/:table` (System Settings).
 * Pass it to `useDualScopeTypes` in place of an organization id.
 */
export const DUAL_SCOPE_GLOBAL = 'global' as const;

/** `'org'` — the selected org's route; `'system'` — the org-less global route. */
export type DualScopeMode = 'org' | 'system';

/** An organization id, or `DUAL_SCOPE_GLOBAL` for the global-only route. */
const dualScopeTypesBase = (scope: string, tableName: DualScopeTableName) =>
  scope === DUAL_SCOPE_GLOBAL
    ? `/api/v1/dual-scope-types/global/${tableName}`
    : `/api/v1/dual-scope-types/orgs/${scope}/${tableName}`;

/** The write base for a mutation: the global route in system mode, else the org route. */
function writeBase(
  scopeMode: DualScopeMode,
  organizationId: string | undefined,
  tableName: DualScopeTableName,
): string {
  return scopeMode === 'system'
    ? dualScopeTypesBase(DUAL_SCOPE_GLOBAL, tableName)
    : dualScopeTypesBase(requireOrg(organizationId), tableName);
}

/**
 * The coded refusals are user-facing rules, not bugs: App.tsx's MutationCache
 * keeps Sentry quiet for them; the hooks' own `onError` toasts them.
 */
export const DUAL_SCOPE_TYPE_MUTATION_META = {
  expectedErrorCodes: Object.values(DUAL_SCOPE_TYPE_ERROR_CODES) as readonly string[],
} as const;

const KNOWN_CODES = new Set<string>(Object.values(DUAL_SCOPE_TYPE_ERROR_CODES));

/** The coded refusal on a failed write (`err.envelope.code`), or `null`. */
export function dualScopeTypeErrorCodeOf(error: unknown): DualScopeTypeErrorCode | null {
  const code = (error as { envelope?: { code?: string } } | null | undefined)?.envelope?.code;
  return code && KNOWN_CODES.has(code) ? (code as DualScopeTypeErrorCode) : null;
}

/** Localised message for a failed write: the coded refusal's copy, else the server message. */
export function dualScopeTypeErrorMessage(error: unknown, fallback: string): string {
  const code = dualScopeTypeErrorCodeOf(error);
  if (code) return i18n.t(`dualScope.errors.${code}`);
  return error instanceof Error && error.message ? error.message : fallback;
}

function requireOrg(organizationId: string | undefined): string {
  if (!organizationId) throw new Error('No organization selected');
  return organizationId;
}

/**
 * The create body: `scope` + only the table's writable columns (`pickFields`).
 * Unset values (`null` / `''`) are left out so the column default applies —
 * a blank `code` in particular, which the route then derives from `name`.
 */
export function buildDualScopeCreateBody(
  tableName: DualScopeTableName,
  data: object & { organization_id: string | null },
): Record<string, unknown> {
  const picked = pickDualScopeFields(data, DUAL_SCOPE_TABLES[tableName].pickFields);
  const body: Record<string, unknown> = {
    scope: data.organization_id === null ? 'global' : 'org',
  };
  for (const [key, value] of Object.entries(picked)) {
    if (value === null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    body[key] = value;
  }
  return body;
}

/** The update body: only the table's updatable columns (`updateFields` — never `code`). */
export function buildDualScopeUpdateBody(
  tableName: DualScopeTableName,
  data: object,
): Record<string, unknown> {
  return pickDualScopeFields(data, DUAL_SCOPE_TABLES[tableName].updateFields);
}

/**
 * Fetch dual-scope types (global + organization-specific) for one list.
 * Pass `DUAL_SCOPE_GLOBAL` instead of an org id for the global rows only
 * (super admins, System Settings) — query key `[table, 'global', activeOnly]`,
 * still under the bare `[table]` prefix every write invalidates.
 *
 * @example
 * ```tsx
 * const { data: assetTypes } = useDualScopeTypes('asset_types', organizationId);
 * ```
 */
export const useDualScopeTypes = <T extends DualScopeType = DualScopeType>(
  tableName: DualScopeTableName,
  /** An organization id, or `DUAL_SCOPE_GLOBAL`. */
  organizationId?: string,
  options?: {
    enabled?: boolean;
    activeOnly?: boolean;
  }
) => {
  const { enabled = true, activeOnly = true } = options || {};

  return useQuery({
    queryKey: [tableName, organizationId, activeOnly],
    queryFn: async (): Promise<T[]> => {
      if (!organizationId) return [];
      const query = activeOnly ? '' : '?includeInactive=true';
      const result = await authedGet<DualScopeTypeListResponse>(
        `${dualScopeTypesBase(organizationId, tableName)}${query}`,
      );
      return (result.items ?? []).map((type) => ({
        ...type,
        is_global: type.organization_id === null,
      })) as unknown as T[];
    },
    enabled: enabled && !!organizationId,
    staleTime: 5 * 60 * 1000, // reference data — 5 minute cache
  });
};

/**
 * The projects index (`['projects', orgId, 'index', {}]`) projects
 * `project_type_name`, so a write to `project_types` must refresh it. A
 * super-admin edit to a GLOBAL type changes rows in every org — hence the
 * bare `['projects']` prefix.
 */
function invalidateProjectsOnTypeChange(queryClient: QueryClient, tableName: string) {
  if (tableName === 'project_types') {
    void queryClient.invalidateQueries({ queryKey: ['projects'] });
  }
}

/**
 * Create a type. `organization_id: null` creates a GLOBAL row (super admins
 * only); an org id creates an org row (org admins only). Only the table's
 * writable columns are sent; a blank `code` is left to the route.
 *
 * @example
 * ```tsx
 * const createType = useCreateDualScopeType('asset_types');
 * createType.mutate({ organization_id: orgId, name: 'Custom Type' });
 * ```
 */
export const useCreateDualScopeType = <
  T extends { name: string } = CreateDualScopeTypeData,
>(
  tableName: DualScopeTableName,
  scopeMode: DualScopeMode = 'org',
) => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();
  const selectedOrgId = selectedOrganization?.id;

  return useMutation({
    meta: DUAL_SCOPE_TYPE_MUTATION_META,
    mutationFn: async (data: T & { organization_id: string | null }) => {
      // Org mode: the path org is the caller's org context (a global create by
      // a super admin there still goes through an org they belong to). System
      // mode: the org-less global route, which creates global rows only.
      const base =
        scopeMode === 'system'
          ? dualScopeTypesBase(DUAL_SCOPE_GLOBAL, tableName)
          : dualScopeTypesBase(requireOrg(data.organization_id ?? selectedOrgId), tableName);
      const result = await authedPost<DualScopeTypeItemResponse>(
        base,
        buildDualScopeCreateBody(tableName, data),
      );
      return result.item as unknown as DualScopeType;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: [tableName] });
      invalidateProjectsOnTypeChange(queryClient, tableName);
      toast.success(i18n.t('dualScope.toast.created', { name: data.name }));
    },
    onError: (error: Error) => {
      toast.error(dualScopeTypeErrorMessage(error, i18n.t('dualScope.toast.createFailed')));
    },
  });
};

/**
 * Update a type. Only the table's updatable columns are sent — `code`,
 * `organization_id` and the system stamps never are.
 *
 * @example
 * ```tsx
 * const updateType = useUpdateDualScopeType('asset_types');
 * updateType.mutate({ id: typeId, name: 'Updated Name' });
 * ```
 */
export const useUpdateDualScopeType = <
  T extends Partial<CreateDualScopeTypeData> = Partial<CreateDualScopeTypeData>,
>(
  tableName: DualScopeTableName,
  scopeMode: DualScopeMode = 'org',
) => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;

  return useMutation({
    meta: DUAL_SCOPE_TYPE_MUTATION_META,
    mutationFn: async (data: T & { id: string }) => {
      const { id, ...updateData } = data;
      const result = await authedPatch<DualScopeTypeItemResponse>(
        `${writeBase(scopeMode, organizationId, tableName)}/${id}`,
        buildDualScopeUpdateBody(tableName, updateData),
      );
      return result.item as unknown as DualScopeType;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: [tableName] });
      invalidateProjectsOnTypeChange(queryClient, tableName);
      toast.success(i18n.t('dualScope.toast.updated', { name: data.name }));
    },
    onError: (error: Error) => {
      toast.error(dualScopeTypeErrorMessage(error, i18n.t('dualScope.toast.updateFailed')));
    },
  });
};

/**
 * Delete a type. A type still referenced anywhere (any org) is refused by the
 * database's NO ACTION foreign keys → 409 `type_in_use` ("untick Active instead").
 *
 * @example
 * ```tsx
 * const deleteType = useDeleteDualScopeType('asset_types');
 * deleteType.mutate(typeId);
 * ```
 */
export const useDeleteDualScopeType = (
  tableName: DualScopeTableName,
  scopeMode: DualScopeMode = 'org',
) => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;

  return useMutation({
    meta: DUAL_SCOPE_TYPE_MUTATION_META,
    mutationFn: async (id: string) => {
      await authedDelete<DualScopeTypeDeleteResponse>(
        `${writeBase(scopeMode, organizationId, tableName)}/${id}`,
      );
      return id;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [tableName] });
      invalidateProjectsOnTypeChange(queryClient, tableName);
      toast.success(i18n.t('dualScope.toast.deleted'));
    },
    onError: (error: Error) => {
      toast.error(dualScopeTypeErrorMessage(error, i18n.t('dualScope.toast.deleteFailed')));
    },
  });
};
